import React from 'react';
import { reduxForm, Field } from 'redux-form';
import { Link } from 'react-router-dom';

import { getCustomerUrl } from 'helpers/url';
import Spinner from 'components/Spinner';
import Form from 'components/Form';
import SelectField from 'components/SelectField';
import CheckboxField from 'components/CheckboxField';
import BaseModal from '../BaseModal';

import styles from './styles.module.scss';
import { can, permActions } from '../../../permissions';
import { formatPlanOptions } from '../../../helpers/subs';

const getGroupedPlanTitles = (plans) => {
  if (!plans) {
    return [];
  }

  const planTitles = Array.from(new Set(plans.map((plan) => plan.name)));
  return planTitles.map((title) => [title, title]);
};

const getPlanPrices = (plans, name) => {
  if (!plans || !name) {
    return [];
  }

  const planPrices = plans
    .filter((plan) => plan.name === name)
    .map((plan) => [plan.price, plan.price]);
  return planPrices;
};

const getPlanByTitleAndPrice = (plans, name, price) => {
  return plans.find((plan) => plan.name === name && plan.price === price);
};

export const transformValues = (plans, values) => {
  const { plan_name, plan_price, ...restValues } = values;
  const plan = getPlanByTitleAndPrice(plans, plan_name, plan_price);
  return { ...restValues, plan: plan.id };
};

let UpgradePlanModal = ({
  overdue = [],
  currentCycle = [],
  plans = [],
  redirectUrl,
  planName,
  sub,
  user,
  isAdminOrManagerOrBillingOrReseller,
  isAdmin,
  submitSucceeded,
  upgradePurchaseId,
  changeField,
  showSplittedForm,
  isOpen,
  closeModal,
  isAtt,
  isQuick,
  loggedInUser,
  ...props
}) => {
  const currentPlans = currentCycle.map((cycle) => cycle.plan);
  let maxDataAvailable = 0;
  if (sub.plan) {
    maxDataAvailable = sub.plan.data_available;
  }

  let injectedPlans = [].concat(plans);

  if (!isAdmin) {
    injectedPlans = injectedPlans.filter(
      (plan) => plan.data_available >= maxDataAvailable,
    );
  }
  const showPrices = can(loggedInUser, permActions.VIEW_PRICES);

  const options = formatPlanOptions(injectedPlans, showPrices);

  const matches = overdue.filter((purchase) => {
    const linePlans = purchase.line_items.map((line) => line.plan);
    const matched = linePlans.filter((plan) => currentPlans.includes(plan));
    return matched.length > 0;
  });

  const url = isAtt
    ? `${getCustomerUrl()}purchase/${upgradePurchaseId}?activate-plan=true`
    : `/purchases/${upgradePurchaseId}/view`;

  const renderIsUpcoming = () => {
    if (!isAtt && !isQuick) {
      return null;
    }

    return (
      <Field component={CheckboxField} name="is_upcoming" label="Is Upcoming" />
    );
  };

  return (
    <BaseModal
      isOpen={isOpen}
      title="Plan Upgrade"
      onClose={closeModal}
      className={styles.modal}
      confirmBtnText=""
      cancelBtnText=""
    >
      {matches.length > 0 && (
        <div className="mar-b">
          The current cycle invoice is unpaid. You may proceed to the update
          plan form.
        </div>
      )}
      {showSplittedForm ? (
        <Form {...props} submit={'Generate invoice'}>
          <Field
            component={SelectField}
            name="plan_name"
            label="Plan Name"
            empty="None"
            options={getGroupedPlanTitles(injectedPlans)}
            onChange={() => {
              changeField('upgradeInvoice', 'plan_price', '');
            }}
          />
          {planName && (
            <Field
              component={SelectField}
              name="plan_price"
              label="Plan Price"
              empty="None"
              options={getPlanPrices(injectedPlans, planName)}
            />
          )}
          {renderIsUpcoming()}
        </Form>
      ) : (
        <Form {...props} submit={'Generate invoice'}>
          <Field
            component={SelectField}
            empty="Select Plan"
            name="plan"
            label="Plans"
            options={options}
          />
          {renderIsUpcoming()}
        </Form>
      )}

      {submitSucceeded && upgradePurchaseId ? (
        <div className="mar-t">
          {isAtt ? (
            <a href={url} target="_blank" rel="noopener noreferrer">
              View Purchase
            </a>
          ) : (
            <Link to={url}>View Purchase</Link>
          )}
        </div>
      ) : null}
      <Spinner showLoader={false} />
    </BaseModal>
  );
};

UpgradePlanModal = reduxForm({
  form: 'upgradeInvoice',
})(UpgradePlanModal);

export default UpgradePlanModal;
